<template lang="pug">
	.ai-music--detail.flex-col.space-medium
		DxTabPanel(:dataSource="tabs")
			template(v-slot:title="{ data }")
				span {{data.name}}
			template(v-slot:item="{ data }")
				.tb-padding
					.flex-col.space-medium(v-if="data.id === 'main'", :class="{ 'disabled-controls': isLoading }")
						.flex-col(v-if="item.Error")
							pre.pre-wrap.error-colored(v-html="item.Error")
						.flex-row.space
							.flex-col.space.flex-5
								.font-small.upper.grey {{$tCaption('Title')}}
								.font-small.grey(v-if="lyricsGenerating") {{$tCaption('Generating...')}}
								DxTextBox(v-else, :value.sync="item.Metadata.Name", stylingMode="filled")
							.flex-col.space.flex-1
								.font-small.upper.grey {{$tCaption('Code')}}
								.font-small.grey(v-if="lyricsGenerating") {{$tCaption('Generating...')}}
								DxTextBox(v-else, :value.sync="item.Metadata.Code", stylingMode="filled")
						.flex-col.space.flex-1
							.font-small.upper.grey {{$tCaption('Artist')}}
							.font-small.grey(v-if="lyricsGenerating") {{$tCaption('Generating...')}}
							DxTextBox(v-else, :value.sync="item.Metadata.Artist", stylingMode="filled")
						.flex-col.space-small.flex-2
							.flex-row.space.align-center
								.font-small.upper.grey {{$tCaption('Category')}}
								Loader(v-if="isLoadingPrompt", text="")
							Dropdown(ref="category", 
								:getTotal="(r) => r.TotalNumberOfRecords", :getItems="(r) => r.Results", :getData="loadCategories",
								:columns="[{ dataField: 'Name', caption: $tCaption('Category') }]",
								displayExpr="Name",
								valueExpr="Oid",
								:selectedItems="item.Metadata.Category && item.Metadata.Category.Oid",
								:showColumnHeaders="false",
								:filtersVisible="false",
								:searchVisible="true",
								:multiple="false",
								stylingMode="filled"
								@select-data="onCategorySelect")
						.flex-col.space.flex-1
							.flex-row.justify.align-center
								.font-small.upper.grey {{$tCaption('Style')}}
								.flex-row.space-small.clickable.hoverable.grey.font-small.align-center(v-if="!lyricsGenerating", @click="regenerateStyle")
									fas(icon="refresh")
									span {{$tCaption('Regenerate style')}}
							.font-small.grey(v-if="lyricsGenerating") {{$tCaption('Generating...')}}
							.flex-row.space.align-center(v-else)
								DxTextArea.flex-1(:value.sync="item.Metadata.Style", stylingMode="filled", :height="80")
								StylesLibrary(@select="v => item.Metadata.Style = v", :currentStyle="item.Metadata.Style")
						.flex-col.space
							.flex-row.justify.align-center
								.font-small.upper.grey {{$tCaption('Lyrics')}}
								.flex-row.space-medium(v-if="!lyricsGenerating")
									.flex-row.space-small.clickable.hoverable.grey.font-small.align-center(@click="regenerateLyrics")
										fas(icon="refresh")
										span {{$tCaption('Regenerate lyrics')}}
									.flex-row.space-small.clickable.hoverable.grey.font-small.align-center(@click="item.Metadata.Lyric = null") 
										fas(icon="times")
										span {{$tCaption('Clear')}}
							.font-small.grey(v-if="lyricsGenerating") {{$tCaption('Generating...')}}
							DxTextArea(v-else, :value.sync="item.Metadata.Lyric", stylingMode="filled", :height="400")
					.flex-col.space-medium(v-if="data.id === 'keywords'")
						KeywordsSelector(:value.sync="item.Metadata.Keywords", :keysActive="true")
						.flex-col.space.flex-1
							.font-small.upper.grey {{$tCaption('Vocals')}}
							DxSelectBox(:value.sync="item.Metadata.Vocals", :data-source="vocals", displayExpr="name", valueExpr="id", stylingMode="filled")
						.flex-col.space.flex-1
							.font-small.upper.grey {{$tCaption('Rating')}}
							RatingControl(:value.sync="item.Rating", :size="25")
					Loader(v-if="isLoading", :fullscreen="true")
					LinkedStations(v-if="item.Oid && data.id === 'links'", :id="item.Oid")
</template>

<script>	
	import DxButton from 'devextreme-vue/button';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxTextArea from 'devextreme-vue/text-area';
	import DxNumberBox from 'devextreme-vue/number-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import LinkedStations from './LinkedStations.vue';
	import DxTabPanel from 'devextreme-vue/tab-panel';
	import KeywordsSelector from './KeywordsSelector.vue';
	import Loader from 'app-core/Loader.vue';
	import { AI_MUSIC_VOCALS } from 'app-core/constants';
	import RatingControl from 'app-core/RatingControl.vue';
	import StylesLibrary from './StylesLibrary.vue';
	import Dropdown from 'app-core/Dropdown.vue';

	export default {
		translationCategory: 'aiMusic.Detail',
		name: 'ai-music-detail',
		components: {
			DxButton, DxTextBox, DxTextArea, DxTabPanel, LinkedStations, KeywordsSelector, Loader, DxNumberBox, DxSelectBox, RatingControl, StylesLibrary,
			Dropdown
		},
		props: ['item'],
		data() {
			return {
				isLoading: 0,
				vocals: AI_MUSIC_VOCALS.toLocale('name', this)
			};
		},
		computed: {
			tabs() {
				return [
					{ id: 'main', name: this.$tCaption('Metadata') },
					{ id: 'keywords', name: this.$tCaption('Keywords') },
					...(this.item.Oid ? [{ id: 'links', name: this.$tCaption('Linked stations') }] : []),
				]
			},
			lyricsGenerating() {
				return this.item.Status === 1;
			}
		},
		watch: {
			
		},
		mounted() {
			if (__DEBUG__)
				window.aiMusicDetail = this;
		},
		destroyed() {
			
		},
		methods: {
			onCategorySelect(v) {
				this.item.Metadata.Category = v;

				if (this.item.Metadata?.Style) {
					if (this.item.Metadata?.Style.indexOf(v?.Name) === -1) {
						this.item.Metadata.Style += `, ${v?.Name}`;
					}
				} else {
					this.item.Metadata.Style = v?.Name;
				}
			},
			loadCategories(pageIndex, limit, opts) {
				let filter = [];
				if (opts?.byKey) {
					filter = ['Oid', '=', opts.byKey];
				}
				let filterCriteria = `${filter && filter.length && `&filterCriteria=${encodeURIComponent(JSON.stringify(filter))}` || ''}`;
				let sorting = `&sorting=[{ selector: 'Name' }]`;
				return this.$Data.getExternalApi('AzureMediaCategory/List', `page=${pageIndex + 1}${filterCriteria}${sorting}`, limit);
			},
			async regenerateLyrics() {
				this.isLoading++;
				const results = (await this.$Data.postExternalApi('OpenAiGpt3/Run', {
					PromptCode: 'LYRICSREGENERATE',
					clientIp: this.$auth.userIP(),
					parameters: {
						Artist: this.item.Metadata.Artist,
						Lyrics: this.item.Metadata.Lyric,
						Style: this.item.Metadata.Style,
						Title: this.item.Metadata.Name,
						Song_Artist: this.item.Metadata.Artist,
						Song_Text: this.item.Metadata.Lyric,
						Song_Style: this.item.Metadata.Style,
						Song_Title: this.item.Metadata.Name
					}
				}).finally(() => this.isLoading--))?.Data?.Content;

				if (__DEBUG__)
					console.debug('regenerateLyrics', { results });

				this.item.Metadata.Lyric = results;
			},
			async regenerateStyle() {
				this.isLoading++;
				const results = (await this.$Data.postExternalApi('OpenAiGpt3/Run', {
					PromptCode: 'LYRICSSTYLEREGENERATE',
					clientIp: this.$auth.userIP(),
					parameters: {
						Artist: this.item.Metadata.Artist,
						Lyrics: this.item.Metadata.Lyric,
						Style: this.item.Metadata.Style,
						Title: this.item.Metadata.Name,
						Song_Artist: this.item.Metadata.Artist,
						Song_Text: this.item.Metadata.Lyric,
						Song_Style: this.item.Metadata.Style,
						Song_Title: this.item.Metadata.Name
					}
				}).finally(() => this.isLoading--))?.Data?.Content;

				if (__DEBUG__)
					console.debug('regenerateStyle', { results });

				this.item.Metadata.Style = results;
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.ai-music--detail {
		
	}
</style>
 